/**
 * Pure helper for building the public B2B-portal URL of a Collection.
 *
 * Used by `CollectionForm`'s "view on portal" line. Mirrors
 * `../Pages/pagePublicUrl.ts` (same portal-language guard), but collections
 * are much simpler than pages: no ancestor chain, no published flag, no
 * hardcoded routes — just `{BASE}/{language}/collections/{permalink}`.
 */

/**
 * Languages the B2B portal actually serves
 * (`vilmers-b2b-portal/src/lib/i18n/config.ts`, `src/middleware.ts`). Any
 * other enabled language (e.g. the admin's `lt`) gets redirected by the
 * portal middleware and 404s.
 */
export const B2B_PORTAL_LANGUAGES = ['en', 'fr', 'de'] as const

const B2B_PORTAL_URL =
  process.env.REACT_APP_B2B_PORTAL_URL || 'http://localhost:8090'

export interface CollectionProfileForUrl {
  language: string
  meta_information?: {
    permalink?: string | null
  } | null
}

export interface CollectionForUrl {
  collection_profiles?: CollectionProfileForUrl[] | null
}

export interface CollectionPublicUrlResult {
  /** `null` when there is nothing sensible to link to yet. */
  url: string | null
  /** Whether `url` actually resolves on the portal right now. */
  clickable: boolean
  /** Short human-readable reason shown next to a disabled/absent URL. */
  reason?: string
}

export function buildCollectionPublicUrl(params: {
  collection: CollectionForUrl | null | undefined
  /** Currently selected admin-UI language, e.g. `en`, `fr`, `de`, `lt`, ... */
  language: string
}): CollectionPublicUrlResult {
  const { collection, language } = params

  const languageSupported = (B2B_PORTAL_LANGUAGES as readonly string[]).includes(
    language
  )
  if (!languageSupported) {
    return {
      url: null,
      clickable: false,
      reason: 'Not available for this language on the portal',
    }
  }

  const profile = collection?.collection_profiles?.find(
    p => p.language === language
  )

  if (!profile) {
    return {
      url: null,
      clickable: false,
      reason: 'No profile for this language yet',
    }
  }

  const permalink = profile.meta_information?.permalink

  if (!permalink) {
    return {
      url: null,
      clickable: false,
      reason: 'No permalink yet — save the collection first',
    }
  }

  return {
    url: `${B2B_PORTAL_URL}/${language}/collections/${permalink}`,
    clickable: true,
  }
}
